/**
 * UnifiedView — single-column inline diff (Word-style "all revisions").
 *
 * Walks the same `AlignedRow` stream as `SideBySideView` but renders one
 * column. Behaviour per row kind:
 *
 *   equal        → render the v2 paragraph plainly (or v1 when v2 is null)
 *   addition     → green ChangeMarker with v2 content
 *   deletion     → red ChangeMarker with v1 content (line-through inside)
 *   modification → two stacked ChangeMarkers: deletion (contentA) followed
 *                  by addition (contentB) so the user reads "old → new" in
 *                  document flow. If `inlineDiff` is provided we instead
 *                  render a single amber block with token-level highlights.
 *   move         → blue ChangeMarker (rendered at the v2 position)
 *
 * The view honours `comparison-visual-language.md` §1.3 inline highlight
 * rules and §6.1 paragraph spacing (mb-4).
 */


import { AnnotationThread } from './AnnotationThread';
import { buildAlignedRows, type AlignedRow } from './alignment';
import { ChangeMarker, type DecisionDisplay } from './ChangeMarker';
import { DecidableChange, displayModeFor, type DecisionRole } from './DecidableChange';
import { InlineDiffView } from './inlineDiff';
import { NodeBlock } from './NodeBlock';
import { UnchangedBlock } from './UnchangedBlock';
import type { DecisionAdapter } from './decisionAdapter';
import type { NavigationAdapter } from './navigationAdapter';
import type { UseAnnotationsResult } from './useAnnotations';
import type { UseFocusReadModeResult } from './useFocusReadMode';
import type { ChangeResponse, ChangeType } from '../../lib/veridoc-api';
import type { DocNode } from '../doc-viewer/DocViewer.types';

interface UnifiedViewProps {
  treeA: DocNode;
  treeB: DocNode;
  changes: ChangeResponse[];
  decisions?: DecisionAdapter;
  navigation?: NavigationAdapter;
  /** Optional focus-mode adapter — see SideBySideView for the contract
   *  (M3 Phase 3.5). */
  focus?: UseFocusReadModeResult;
  /** Optional annotation hook — when supplied, each change row gets an
   *  inline expand thread (M4 Phase 4.4). */
  annotations?: UseAnnotationsResult;
}

export function UnifiedView({
  treeA,
  treeB,
  changes,
  decisions,
  navigation,
  focus,
  annotations,
}: UnifiedViewProps): React.JSX.Element {
  if (treeA.type !== 'doc' || treeB.type !== 'doc') {
    return (
      <div className="rounded-md border border-semantic-danger bg-semantic-dangerBg p-4 text-sm text-ink-900">
        预期 doc 根节点，但收到了非法输入
      </div>
    );
  }

  const rows = buildAlignedRows(treeA.children, treeB.children, changes);

  return (
    <article className="mx-auto max-w-doc-column px-10 font-serif text-md leading-relaxed text-ink-900">
      {rows.map((row) => (
        <RowWithAnnotations
          key={row.key}
          row={row}
          decisions={decisions}
          navigation={navigation}
          focus={focus}
          annotations={annotations}
        />
      ))}
    </article>
  );
}

function RowWithAnnotations(props: {
  row: AlignedRow;
  decisions?: DecisionAdapter;
  navigation?: NavigationAdapter;
  focus?: UseFocusReadModeResult;
  annotations?: UseAnnotationsResult;
}): React.JSX.Element | null {
  const rendered = RowRenderer(props);
  if (!rendered) return null;
  if (!props.row.change || !props.annotations) return rendered;
  // Anchor only for change-backed rows so the perf script can count and
  // target decisions; equal paragraphs (no change) don't need the anchor.
  return (
    <div data-testid="change-row" data-change-id={props.row.change.id}>
      {rendered}
      <AnnotationThread
        changeId={props.row.change.id}
        annotations={props.annotations}
      />
    </div>
  );
}

function RowRenderer({
  row,
  decisions,
  navigation,
  focus,
}: {
  row: AlignedRow;
  decisions?: DecisionAdapter;
  navigation?: NavigationAdapter;
  focus?: UseFocusReadModeResult;
  annotations?: UseAnnotationsResult;
}): React.JSX.Element | null {
  // Same wrapping pattern as SideBySideView: only wrap rows backed by a
  // change record, and only when an adapter was supplied. The unified
  // view shows a single column, so the chip role mostly tracks the side
  // that carries content (addition='B', deletion='A', modification='B',
  // move='B'). For the modification fallback (stacked deletion + addition
  // when no inlineDiff is present) we still wrap the pair once with
  // role='B' since the decision is one click; the inner deletion strip
  // additionally gets `decisionDisplay='loser'` when accepted because
  // accepting a modification means the old paragraph goes away.
  const wrap = (
    renderInner: (display: DecisionDisplay) => React.JSX.Element,
    opts: { changeType: ChangeType; role: DecisionRole },
  ): React.JSX.Element => {
    if (!row.change || !decisions) {
      return renderInner('pending');
    }
    const status = decisions.getStatus(row.change.id);
    const display = displayModeFor(status, opts.role);
    return (
      <DecidableChange
        changeId={row.change.id}
        changeType={opts.changeType}
        status={status}
        meta={decisions.getMeta(row.change.id)}
        pending={decisions.isPending(row.change.id)}
        canDecide={decisions.canDecide}
        onDecide={(action, note) => decisions.decide(row.change!.id, action, note)}
        navigation={navigation}
        role={opts.role}
      >
        {renderInner(display)}
      </DecidableChange>
    );
  };

  switch (row.kind) {
    case 'equal': {
      // Prefer v2's representation; fall back to v1 (e.g. when right ran
      // ahead of left at the document tail).
      const block = row.rightBlock ?? row.leftBlock;
      if (!block) return null;
      return (
        <UnchangedBlock
          nodeKey={row.key}
          enabled={focus?.enabled ?? false}
          dimmed={focus?.isTempActive(row.key) ?? false}
          tempActivate={focus?.tempActivate}
        >
          <NodeBlock node={block} />
        </UnchangedBlock>
      );
    }

    case 'addition':
      return wrap(
        (display) => (
          <ChangeMarker changeType="addition" decisionDisplay={display}>
            {row.rightBlock && <NodeBlock node={row.rightBlock} />}
          </ChangeMarker>
        ),
        { changeType: 'addition', role: 'B' },
      );

    case 'deletion':
      return wrap(
        (display) => (
          <ChangeMarker changeType="deletion" decisionDisplay={display}>
            {row.leftBlock && (
              <p className="mb-0 font-serif text-md leading-relaxed line-through decoration-change-deletion-bar decoration-2">
                <NodeBlockText node={row.leftBlock} />
              </p>
            )}
          </ChangeMarker>
        ),
        { changeType: 'deletion', role: 'A' },
      );

    case 'modification': {
      const inline = row.change?.inlineDiff;
      if (inline && inline.length > 0) {
        return wrap(
          (display) => (
            <ChangeMarker changeType="modification" decisionDisplay={display}>
              <p className="mb-0 font-serif text-md leading-relaxed">
                <InlineDiffView segments={inline} side="both" />
              </p>
            </ChangeMarker>
          ),
          { changeType: 'modification', role: 'B' },
        );
      }
      // Fallback when the engine did not produce an inline diff: stack
      // deletion-then-addition so the reader sees old → new in order. We
      // wrap them in a single DecidableChange (role='B' — the addition
      // is the side that wins on accept). The two ChangeMarkers receive
      // matching decisionDisplay states so the deletion fades out when
      // the user accepts and the addition fades out when they reject.
      return wrap(
        (display) => {
          // Compose per-strip displays: in unified fallback the deletion
          // strip mirrors A's fate (winner if `display==='loser'` —
          // i.e. user rejected — because then the original paragraph
          // stays) and the addition strip mirrors B's fate.
          const deletionDisplay: DecisionDisplay =
            display === 'pending'
              ? 'pending'
              : display === 'winner'
                ? 'loser' // accept → old paragraph gone
                : 'winner'; // reject → old paragraph stays
          const additionDisplay: DecisionDisplay = display;
          return (
            <>
              <ChangeMarker
                changeType="deletion"
                compact
                decisionDisplay={deletionDisplay}
              >
                {row.leftBlock && (
                  <p className="mb-0 font-serif text-md leading-relaxed line-through decoration-change-deletion-bar decoration-2">
                    <NodeBlockText node={row.leftBlock} />
                  </p>
                )}
              </ChangeMarker>
              <ChangeMarker
                changeType="addition"
                decisionDisplay={additionDisplay}
              >
                {row.rightBlock && <NodeBlock node={row.rightBlock} />}
              </ChangeMarker>
            </>
          );
        },
        { changeType: 'modification', role: 'B' },
      );
    }

    case 'move':
      return wrap(
        (display) => (
          <ChangeMarker changeType="move" decisionDisplay={display}>
            {row.rightBlock && <NodeBlock node={row.rightBlock} />}
          </ChangeMarker>
        ),
        { changeType: 'move', role: 'B' },
      );

    case 'format': {
      // Format changes: identical text, bold/italic differs. Show inline style
      // diff in the unified view so the reviewer sees what changed.
      const inline = row.change?.inlineDiff;
      return wrap(
        (display) => (
          <ChangeMarker changeType="format" decisionDisplay={display}>
            {inline && inline.length > 0 ? (
              <p className="mb-0 font-serif text-md leading-relaxed">
                <InlineDiffView segments={inline} side="both" />
              </p>
            ) : (
              row.rightBlock && <NodeBlock node={row.rightBlock} />
            )}
          </ChangeMarker>
        ),
        { changeType: 'format', role: 'B' },
      );
    }

    default: {
      const _exhaustive: never = row.kind;
      void _exhaustive;
      return null;
    }
  }
}

/**
 * Renders just the inline runs of a node — no wrapping block element. Used
 * when we want to draw a deletion strip with our own line-through styling
 * rather than NodeBlock's default paragraph.
 */
function NodeBlockText({ node }: { node: DocNode }): React.JSX.Element {
  return (
    <>
      {node.runs.map((run, idx) => (
        <span key={idx}>{run.text}</span>
      ))}
    </>
  );
}

export default UnifiedView;
